import type {
  PhoneDto,
  SubscriberEmailAddress,
  SubscriberReadModel,
} from "@/.generated/hey/backoffice-backend/types.gen";
import type { SubscriberMail, SubscriberModel } from "@/domains/subscriber/subscriber.types";
import type { TPhoneDto } from "@/types/backoffice/dto/subscriber/phone-dto";

import { brandNumber } from "@/lib/branded";
import { EMyVoltalisAccess } from "@/types/backoffice/dto/subscriber/subscriber-dto";

export function mapSubscriber(subscriber: SubscriberReadModel): SubscriberModel {
  return {
    id: brandNumber(subscriber.id),
    civility: subscriber.title,
    firstname: subscriber.firstname,
    lastname: subscriber.lastname,
    myVoltalisAccess: subscriber.myVoltalis.access ? convertMyVoltalisAccess(subscriber.myVoltalis.access) : null,
    // TODO : Not an error, this will be properly named when re-do the SubscriberModel
    hasPassword: subscriber.myVoltalis.canBeImpersonated,
    privileged: subscriber.flags.privileged,
    vip: subscriber.flags.vip,
    validPhoneList: ensureDefaultPhone(
      subscriber.phones.map(convertPhone).filter((phone): phone is TPhoneDto => phone !== null),
    ),
    validMailList: subscriber.mails.map(convertMail),
  };
}

// Legacy subscribers may have no phone flagged as default (the field didn't exist before ~2022).
// In that case, fall back to treating the first phone in the list as the default one for display purposes.
export function ensureDefaultPhone(phones: TPhoneDto[]): TPhoneDto[] {
  if (phones.length === 0 || phones.some((phone) => phone.default)) {
    return phones;
  }

  return phones.map((phone, idx) => (idx === 0 ? { ...phone, default: true } : phone));
}

function convertMyVoltalisAccess(access: NonNullable<SubscriberReadModel["myVoltalis"]["access"]>): EMyVoltalisAccess {
  switch (access) {
    case "MY_VOLTALIS_2016":
      return EMyVoltalisAccess.MY_VOLTALIS_2016;
    case "MY_VOLTALIS_2022":
      return EMyVoltalisAccess.MY_VOLTALIS_2022;
    default:
      throw new Error(`Unknown MyVoltalis access: ${String(access)}`);
  }
}

function convertPhone(phone: PhoneDto): TPhoneDto | null {
  if (phone.id == null || phone.phone == null) {
    return null;
  }

  return {
    id: phone.id,
    phone: phone.phone,
    valid: !!phone.valid,
    default: !!phone.default,
  };
}

function convertMail(mail: SubscriberEmailAddress): SubscriberMail {
  return {
    id: brandNumber(mail.id),
    mail: mail.mail,
    default: mail.default,
  };
}
